// src/pages/Documents.js
/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HelmetTemplate from '../utils/HelmetTemplate';
import WebpageShell from './WebpageShell';
import './Documents.css'; // keeps your document-content styles

export default function Documents() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { documentId } = useParams();
  const navigate = useNavigate();

  const processHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // H1s (from p.c6) → mobile: text-lg, desktop: text-base
    doc.querySelectorAll('p.c6').forEach((p) => {
      const h1 = document.createElement('h1');
      h1.innerHTML = p.innerHTML;
      h1.className = 'text-lg md:text-base font-extrabold text-gray-600 mb-4 mt-0 md:mt-8';
      p.parentNode.replaceChild(h1, p);
    });

    // Either paragraphs or H2s (from p.c0)
    doc.querySelectorAll('p.c0').forEach((p) => {
      const boldSpans = p.querySelectorAll('span.c3, span.c5.c3, span.c4.c3, span.c3.c4, span.c2');
      if (boldSpans.length > 0) {
        const bold = boldSpans[0];
        const boldText = bold.textContent.trim();
        const paraText = p.textContent.trim();
        // Treat as heading if it's mostly bold or marked like c4 (your prior heuristic)
        if (boldText.length > paraText.length * 0.8 || bold.classList.contains('c4')) {
          const h2 = document.createElement('h2');
          h2.textContent = paraText;
          // mobile: text-lg, desktop: text-base
          h2.className = 'text-lg md:text-base font-extrabold text-gray-600 mb-4 mt-0 md:mt-8';
          p.parentNode.replaceChild(h2, p);
          return;
        }
      }
      // Regular paragraph → inherit container size; add md:text-sm just in case
      p.className = 'mb-4 text-gray-800 leading-relaxed md:text-sm';
    });

    // Remove empty lines
    doc.querySelectorAll('p.c7').forEach((p) => {
      if (!p.textContent.trim()) p.remove();
    });

    return doc.body.innerHTML;
  };

  useEffect(() => {
    const fetchDocument = async () => {
      if (!documentId) {
        navigate('/');
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const documentUrl =
          `https://firebasestorage.googleapis.com/v0/b/match-medic-p0.appspot.com/o/corporate%2F${documentId}.html?alt=media`;
        const response = await fetch(documentUrl);
        if (!response.ok) throw new Error(`Failed to fetch document: ${response.statusText}`);
        const html = await response.text();
        setContent(processHtml(html));
      } catch (e) {
        console.error('Error fetching document:', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDocument();
  }, [documentId, navigate]);

  const getPageTitle = () => {
    if (!documentId) return 'Document';
    return documentId
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  return (
    <WebpageShell
      navProps={{ addAnnouncementBar: false, isRecPrototypePage: false }}
      showCTA={true}
      showFooter={true}
    >
      {/* SEO */}
      <HelmetTemplate
        title={`${getPageTitle()} - Match Medic`}
        description={`Read the ${getPageTitle()} for Match Medic.`}
        canonicalUrl={`https://matchmedic.com/documents/${documentId}`}
      />

      {/* Page content */}
      <div className="w-full md:w-4/5 mx-auto pt-6 md:pt-10 pb-10">
        {/* Mobile: full-bleed, transparent, no shadow.
            Desktop: contained, gray bg, rounded, shadow. */}
        <div
          className="
            mx-[calc(50%-50vw)] w-screen md:mx-auto md:w-auto
            md:max-w-5xl
            bg-transparent md:bg-gray-100
            shadow-none md:shadow-lg
            rounded-none md:rounded-lg
          "
        >
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mm_blue" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Document Not Found</h2>
              <p className="text-gray-600 mb-6">
                The document you&apos;re looking for could not be loaded. Please try again later.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-mm_blue hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div
              // Inherit: mobile default (unchanged), desktop → text-sm.
              // Headings override to md:text-base above.
              className="px-4 pt-0 pb-6 md:px-24 md:pt-8 md:pb-8 document-content md:text-sm"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </div>
      </div>
    </WebpageShell>
  );
}
